import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Loader2, RotateCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { clientAdjustmentApi, commercialCyclesApi, CommercialCycle, CyclePreview, CycleRunProgressDto, GenerateDraftRun } from "@/lib/api";
import { CycleAlreadyGeneratedError, isCycleAlreadyGeneratedError } from "@/lib/cycleError";
import { ActiveGenerateDraftRun } from "./generateDraftRun";
import { CYCLE_QUERY_KEYS, RUN_STATUS_BADGE, formatDateTimeValue as formatRunDate } from "./constants";
import { Progress } from "@/components/ui/progress";

/**
 * O backend manda cada conflito como uma única string descritiva, ex.:
 * "252 cliente(s) sem reajuste cadastrado ... para o ciclo 2028 (...): 10, 67, 72, ..."
 * Separa a mensagem da lista de IDs (depois do primeiro ":").
 */
/**
 * Coluna "Resultado" do histórico — cobre também os status de agendamento.
 * Para `Triggered`, busca o resultado real (`triggeredRunId`) dentro da própria lista já
 * carregada (o backend já retorna a linha do agendamento e a do run real na mesma consulta
 * por par), evitando uma requisição extra.
 */
function renderRunResult(run: GenerateDraftRun, historyRuns: GenerateDraftRun[]): ReactNode {
  switch (run.status) {
    case "Completed":
      return `${run.generatedCount} criados, ${run.skippedCount} ignorados`;
    case "Failed":
      return run.errorMessage || "Falhou";
    case "Running":
      return "Em execução...";
    case "Scheduled":
      return "Aguardando disparo";
    case "Cancelled":
      return run.cancelledBy ? `Cancelado por ${run.cancelledBy}` : "Cancelado";
    case "Triggered": {
      const realRun = historyRuns.find((r) => r.runId === run.triggeredRunId);
      if (!realRun) return "Execução real iniciada — atualize para ver o resultado.";
      const realBadge = RUN_STATUS_BADGE[realRun.status];
      return (
        <span className="inline-flex flex-wrap items-center gap-1">
          Resultado: <Badge variant={realBadge.variant} className="text-[10px]">{realBadge.label}</Badge>
          {realRun.status === "Completed" && `${realRun.generatedCount} criados, ${realRun.skippedCount} ignorados`}
          {realRun.status === "Failed" && (realRun.errorMessage || "Falhou")}
        </span>
      );
    }
    default:
      return null;
  }
}

function parseCyclePreviewConflict(conflict: string): { message: string; clientIds: string[] } {
  const separatorIndex = conflict.indexOf(":");
  if (separatorIndex === -1) return { message: conflict, clientIds: [] };
  return {
    message: `${conflict.split(" ")[0]} Cliente(s) sem reajuste cadastrado para o ciclo de destino — entrarão com 0% de reajuste caso prossiga.`,
    clientIds: conflict
      .slice(separatorIndex + 1)
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean),
  };
}

function CollapsibleClientIds({
  label,
  ids,
  clientNameById,
}: {
  label: string;
  ids: (string | number)[];
  clientNameById: Map<number, string>;
}) {
  const [open, setOpen] = useState(false);
  if (ids.length === 0) return <p className="text-sm text-yellow-800">{label}</p>;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border border-yellow-200 bg-yellow-50">
      <CollapsibleTrigger className="flex w-full items-start gap-2 p-2 text-left text-sm text-yellow-800">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span className="flex-1">{label}</span>
        {open ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-2 pb-2">
        <div className="flex max-h-40 flex-wrap gap-1 overflow-y-auto rounded-md bg-white/60 p-2">
          {ids.map((id) => (
            <Badge key={id} variant="outline" className="border-yellow-300 text-xs text-yellow-800">
              {clientNameById.get(Number(id)) || `Cliente #${id}`}
            </Badge>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface GenerateDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cycles: CommercialCycle[];
  onGenerated: () => void;
  /** Run atualmente rastreado (pode ser de qualquer par, disparado nesta tela ou detectado via histórico). */
  activeRun: ActiveGenerateDraftRun | null;
  run: GenerateDraftRun | null;
  isGenerating: boolean;
  /** Progresso granular do run em execução (vem do pai, sem polling duplicado). */
  progress: CycleRunProgressDto | null;
  onRunStarted: (run: ActiveGenerateDraftRun) => void;
  onRunCleared: () => void;
}


export function GenerateDraftDialog({
  open,
  onOpenChange,
  cycles,
  onGenerated,
  activeRun,
  run,
  isGenerating,
  progress,
  onRunStarted,
  onRunCleared,
}: GenerateDraftDialogProps) {

  const [sourceYear, setSourceYear] = useState<string>("");
  const [targetYear, setTargetYear] = useState<string>("");
  const [preview, setPreview] = useState<CyclePreview | null>(null);
  const [alreadyGeneratedError, setAlreadyGeneratedError] = useState<CycleAlreadyGeneratedError | null>(null);

  // Usado só para trocar IDs por nome nos painéis de conflito/resultado.
  const { data: clientNameById = new Map<number, string>() } = useQuery({
    queryKey: ["all-clients-for-name-lookup"],
    queryFn: async () => {
      const clients = await clientAdjustmentApi.getAllClients();
      return new Map(clients.map((c) => [c.clientId, c.clientFantasyName || c.clientCompanyName]));
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const resetForm = () => {
    setSourceYear("");
    setTargetYear("");
    setPreview(null);
    setAlreadyGeneratedError(null);
  };

  const previewMutation = useMutation({
    mutationFn: () => commercialCyclesApi.preview(Number(sourceYear), Number(targetYear)),
    onSuccess: (data) => setPreview(data),
    onError: () => toast.error("Erro ao gerar o preview do tombamento."),
  });

  // Histórico do par selecionado — vem do backend ordenado do mais recente pro mais antigo.
  // É a fonte usada para: (1) detectar se já há algo "Running" para esse par (disparado por
  // qualquer admin, não só nesta sessão) e (2) bloquear reexecução se o par já foi "Completed".
  const { data: historyRuns = [] } = useQuery<GenerateDraftRun[]>({
    queryKey: CYCLE_QUERY_KEYS.generateDraftRunsByPair(sourceYear, targetYear),
    queryFn: async () => {
      const response = await commercialCyclesApi.listGenerateDraftRuns(Number(sourceYear), Number(targetYear));
      return response.data || [];
    },
    enabled: !!sourceYear && !!targetYear,
    refetchInterval: 15000,
  });

  const latestRun = historyRuns[0] ?? null;
  const completedRun = historyRuns.find((r) => r.status === "Completed") ?? null;

  // Se o par selecionado já tem uma execução em andamento (minha ou de outro admin,
  // inclusive após um reload sem runId salvo), passa a acompanhá-la.
  useEffect(() => {
    if (latestRun?.status === "Running" && activeRun?.runId !== latestRun.runId) {
      onRunStarted({ runId: latestRun.runId, sourceYear: latestRun.sourceYear, targetYear: latestRun.targetYear });
    }
  }, [latestRun, activeRun?.runId, onRunStarted]);

  const generateMutation = useMutation({
    mutationFn: () => commercialCyclesApi.generateDraft(Number(sourceYear), Number(targetYear)),
    onSuccess: (data) => onRunStarted({ runId: data.runId, sourceYear: data.sourceYear, targetYear: data.targetYear }),
    onError: (error) => {
      const already = isCycleAlreadyGeneratedError(error);
      if (already) {
        setAlreadyGeneratedError(already);
      } else {
        toast.error("Erro ao gerar o rascunho do ciclo.");
      }
    },
  });

  // O run rastreado (`activeRun`/`run`) pode ser de outro par — só o exibimos aqui
  // quando ele corresponde exatamente ao par selecionado nos selects.
  const runMatchesSelection = !!activeRun && !!sourceYear && !!targetYear
    && activeRun.sourceYear === Number(sourceYear) && activeRun.targetYear === Number(targetYear);
  const isGeneratingSelected = runMatchesSelection && isGenerating;
  const runForSelected = runMatchesSelection ? run : null;
  const finishedSelected = runForSelected?.status === "Completed" || runForSelected?.status === "Failed";
  const anotherRunInProgress = isGenerating && !runMatchesSelection;

  const blocked = !!alreadyGeneratedError || (!runMatchesSelection && !!completedRun) || anotherRunInProgress;

  const handleRetry = () => {
    onRunCleared();
    setAlreadyGeneratedError(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleDismissFinished = () => {
    onRunCleared();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : handleClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Gerar rascunho de ciclo</DialogTitle>
          <DialogDescription>
            Copia os vínculos de produto do ciclo de origem para o ciclo de destino (tombamento aditivo).
            A operação é idempotente — pode ser executada novamente sem duplicar dados.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Ciclo de origem</Label>
            <Select
              value={sourceYear}
              onValueChange={(v) => {
                setSourceYear(v);
                setTargetYear(String(Number(v) + 1));
                setPreview(null);
                setAlreadyGeneratedError(null);
              }}
              disabled={isGeneratingSelected}
            >
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {cycles.map((c) => (
                  <SelectItem key={c.year} value={String(c.year)}>{c.year} ({c.status})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Ciclo de destino</Label>
            <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
              {targetYear || "—"}
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          O ciclo de destino não precisa existir ainda — ele é criado ao gerar o rascunho.
        </p>

        {sourceYear && targetYear && historyRuns.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Execuções anteriores para este par</Label>
            <div className="rounded-md border max-h-40 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Início</TableHead>
                    <TableHead>Por</TableHead>
                    <TableHead>Resultado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyRuns.map((r) => (
                    <TableRow key={r.runId}>
                      <TableCell>
                        <Badge variant={RUN_STATUS_BADGE[r.status].variant}>{RUN_STATUS_BADGE[r.status].label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.startedAt ? formatRunDate(r.startedAt) : r.scheduledAt ? `Para ${formatRunDate(r.scheduledAt)}` : "—"}
                      </TableCell>
                      <TableCell className="text-xs">{r.triggeredBy || "—"}</TableCell>
                      <TableCell className="text-xs">{renderRunResult(r, historyRuns)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {alreadyGeneratedError && (
          <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Este ciclo já foi gerado anteriormente (execução #{alreadyGeneratedError.completedRunId}). Não é
              possível gerar novamente para {alreadyGeneratedError.sourceYear} → {alreadyGeneratedError.targetYear}.
            </div>
          </div>
        )}

        {!alreadyGeneratedError && !runMatchesSelection && completedRun && (
          <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Este par já foi gerado com sucesso (execução #{completedRun.runId}, por {completedRun.triggeredBy || "—"}).
              Escolha outro ciclo de destino para gerar novamente.
            </div>
          </div>
        )}

        {anotherRunInProgress && activeRun && (
          <div className="flex items-start gap-2 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Já existe um rascunho em geração para {activeRun.sourceYear} → {activeRun.targetYear}. Aguarde a
              conclusão antes de iniciar outro.
            </div>
          </div>
        )}

        {preview && !isGeneratingSelected && !finishedSelected && (
          <div className="rounded-md border p-3 space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {/* <div>Clientes com vínculo: <strong>{preview.totalProfileClients}</strong></div> */}
              <div>Total de clientes: <strong>{preview.totalClients}</strong></div>
              <div>Total de produtos: <strong>{preview.totalProducts}</strong></div>
              <div>Total de linhas: <strong>{preview.totalProfileClients}</strong></div>
            </div>
            <div className="mb-4">Processado: <strong>{preview.alreadyGenerated} de {preview.totalProfileClients} - {preview.alreadyGenerated / preview.totalProfileClients * 100}%</strong></div>
            {preview.conflicts.length > 0 && (
              <div className="space-y-2">
                {preview.conflicts.map((conflict, index) => {
                  const { message, clientIds } = parseCyclePreviewConflict(conflict);
                  return (
                    <CollapsibleClientIds
                      key={index}
                      label={message}
                      ids={clientIds}
                      clientNameById={clientNameById}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {isGeneratingSelected && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Gerando rascunho do ciclo…
            </div>
            <Progress value={progress?.percentComplete ?? 0} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {progress
                ? `${progress.processed} de ${progress.totalExpected} itens (${Math.round(progress.percentComplete)}%)`
                : "Iniciando..."}
            </p>
          </div>
        )}

        {runForSelected?.status === "Completed" && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900 space-y-1">
            <div className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4" /> Rascunho gerado com sucesso
            </div>
            <div>{runForSelected.generatedCount} vínculo(s) criado(s), {runForSelected.skippedCount} ignorado(s).</div>
            {runForSelected.clientsWithoutAdjustment.length > 0 && (
              <CollapsibleClientIds
                label={`${runForSelected.clientsWithoutAdjustment.length} cliente(s) sem reajuste cadastrado — entraram com 0%.`}
                ids={runForSelected.clientsWithoutAdjustment}
                clientNameById={clientNameById}
              />
            )}
          </div>
        )}

        {runForSelected?.status === "Failed" && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive space-y-1">
            <div className="flex items-center gap-2 font-medium">
              <XCircle className="h-4 w-4" /> Falha ao gerar o rascunho
            </div>
            <div>{runForSelected.errorMessage || "Erro desconhecido."}</div>
            <div className="text-xs opacity-80">
              Nada ficou pela metade — a operação foi revertida automaticamente. Você pode tentar novamente.
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {alreadyGeneratedError || finishedSelected ? (
            <>
              {runForSelected?.status === "Failed" && (
                <Button variant="outline" onClick={handleRetry}>
                  <RotateCcw className="h-4 w-4 mr-2" /> Tentar novamente
                </Button>
              )}
              <Button onClick={handleDismissFinished}>Fechar</Button>
            </>
          ) : !preview ? (
            <Button
              disabled={!sourceYear || !targetYear || previewMutation.isPending}
              onClick={() => previewMutation.mutate()}
            >
              {previewMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Analisar
            </Button>
          ) : (
            <>
              {isGeneratingSelected && (
                <Button variant="outline" onClick={handleClose}>
                  Fechar (continua em segundo plano)
                </Button>
              )}
              <Button
                disabled={
                  preview.alreadyGenerated === preview.totalProfileClients ||
                  generateMutation.isPending ||
                  isGeneratingSelected ||
                  blocked
                }
                onClick={() => generateMutation.mutate()}
              >
                {(generateMutation.isPending || isGeneratingSelected) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmar geração
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
